import React from 'react';
import { Container, Typography, Grid, Paper, Box, List, ListItem, ListItemText, ListItemButton, Button, CircularProgress, Alert, Collapse, IconButton, Divider, Chip, Stack, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ExpandLess, ExpandMore, Refresh as RefreshIcon, Schedule as ScheduleIcon, EventAvailable as EventAvailableIcon, SportsBasketball as SportsIcon, CalendarToday as CalendarIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useMemberContext } from '../../hooks/useMemberContext';
import useDashboardBookings from '../../hooks/useDashboardBookings';
import { useFailedTransactions } from '../../hooks/useFailedTransactions';
import BookingCard from './BookingCard';
import { classAPI } from '../../services/api';
import { formatTimeString, formatDate, formatDateForInput } from '../../utils/dateUtils';


const MemberDashboard: React.FC = () => {
  const { user, memberProfile } = useMemberContext();
  const { todaysBookings, upcomingBookings, loading, error, refetch } = useDashboardBookings();
  const { hasFailedTransactions, loading: failedTransactionsLoading } = useFailedTransactions(memberProfile?.id);
  const [todaysExpanded, setTodaysExpanded] = React.useState(true);
  const [upcomingExpanded, setUpcomingExpanded] = React.useState(false);
  const [todaysClasses, setTodaysClasses] = React.useState<any[]>([]);
  const [classesLoading, setClassesLoading] = React.useState(false);
  const [classesError, setClassesError] = React.useState<string | null>(null);
  const [courtDialogOpen, setCourtDialogOpen] = React.useState(false);
  const [courtType, setCourtType] = React.useState<string>('');
  const [courtDate, setCourtDate] = React.useState<string>(formatDateForInput(new Date()));
  const [courtTime, setCourtTime] = React.useState<string>('');
  const [alertDialogOpen, setAlertDialogOpen] = React.useState(false);

  React.useEffect(() => {
    if (memberProfile?.alertEnabled) {
      setAlertDialogOpen(true);
    }
  }, [memberProfile?.alertEnabled]);

  const fetchTodaysClasses = async () => {
    setClassesLoading(true);
    setClassesError(null);
    try {
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const todayStr = today.toISOString().split('T')[0];
      const tomorrowStr = tomorrow.toISOString().split('T')[0];

      // Use date range to get today and tomorrow's classes
      const response = await classAPI.getClassInstances({
        startDate: todayStr,
        endDate: tomorrowStr,
        includeBookings: 'true'
      });
      const allInstances = response.data || [];

      // Filter to show only upcoming classes (future time today, all of tomorrow)
      const now = new Date();
      const currentHours = now.getHours();
      const currentMinutes = now.getMinutes();

      const upcomingInstances = allInstances.filter((instance: any) => {
        // Filter out cancelled or inactive classes
        if (instance.status?.toUpperCase() === 'CANCELLED' || instance.active === false) {
          return false;
        }

        // Check if class is today or tomorrow
        const instanceDate = instance.sessionDate;
        const isToday = instanceDate === todayStr;
        const isTomorrow = instanceDate === tomorrowStr;

        // Include all tomorrow's classes
        if (isTomorrow) return true;

        // For today's classes, filter by time
        if (isToday) {
          const startTime = instance.startTime || '';
          if (!startTime) return true; // Include if no time specified

          try {
            // Strip timezone suffix and parse just the time part
            // startTime format: "HH:MM:SS+TZ" or "HH:MM:SS-TZ"
            const timePart = startTime.split(/[+-]/)[0]; // Get just "HH:MM:SS"
            const [hours, minutes] = timePart.split(':').map(Number);

            // Direct comparison: is the class time in the future?
            const isUpcoming = hours > currentHours || (hours === currentHours && minutes > currentMinutes);

            return isUpcoming;
          } catch (err) {
            console.error('Time parsing error:', err);
            return true; // Include if time parsing fails
          }
        }

        return false;
      });

      // Already sorted by backend (sessionDate ASC, startTime ASC)
      setTodaysClasses(upcomingInstances);
    } catch (err: any) {
      console.error('Error fetching class instances:', err);
      setClassesError(err?.message || 'Failed to load class instances');
    } finally {
      setClassesLoading(false);
    }
  };

  React.useEffect(() => {
    fetchTodaysClasses();
  }, []);

  const navigate = useNavigate();

  const handleBookClass = () => {
    navigate('/classes');
  };

  const handleBookCourt = () => {
    setCourtDialogOpen(true);
  };

  const handleCourtBookingSubmit = () => {
    if (!courtType) {
      return;
    }

    setCourtDialogOpen(false);

    // Build navigation URL with query parameters
    const params = new URLSearchParams();
    params.append('type', courtType.toLowerCase());
    if (courtDate) {
      params.append('date', courtDate);
    }
    if (courtTime) {
      params.append('time', courtTime);
    }

    navigate(`/courts-calendar?${params.toString()}`);

    // Reset form
    setCourtType('');
    setCourtDate(formatDateForInput(new Date()));
    setCourtTime('');
  };

  const handleCourtDialogClose = () => {
    setCourtDialogOpen(false);
    setCourtType('');
    setCourtDate(formatDateForInput(new Date()));
    setCourtTime('');
  };

  const handleCourtSelection = (courtType: string) => {
    setCourtDialogOpen(false);
    navigate(`/amenities?type=${courtType.toLowerCase()}`);
  };

  const handleTodaysSchedule = () => {
    const today = new Date().toISOString().split('T')[0];
    navigate(`/calendar?date=${today}`);
  };

  // Helper to format time as requested: remove leading zero and remove space before AM/PM
  const formatTimeDisplay = (timeStr: string) => {
    if (!timeStr) return '';
    const formatted = formatTimeString(timeStr);
    // Remove leading zero like 04: -> 4:
    const noLeadingZero = formatted.replace(/^0(?=\d:)/, '');
    // Remove space before AM/PM and uppercase
    return noLeadingZero.replace(/\s+/g, '').toUpperCase();
  };

  // Compute duration between start and end time strings (HH:MM:SS or HH:MM)
  const computeDurationDisplay = (start?: string, end?: string, durationField?: any) => {
    if (durationField) {
      // If duration provided as minutes or string like '1h'
      if (typeof durationField === 'number') {
        if (durationField >= 60 && durationField % 60 === 0) return `${durationField / 60}h`;
        if (durationField >= 60) return `${Math.floor(durationField / 60)}h ${durationField % 60}m`;
        return `${durationField}m`;
      }
      return String(durationField);
    }

    if (!start || !end) return '';
    try {
      // strip timezone suffix
      const strip = (s: string) => s.split(/\+|-/)[0];
      const [sh, sm] = strip(start).split(':').map(Number);
      const [eh, em] = strip(end).split(':').map(Number);
      const now = new Date();
      const sd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), sh || 0, sm || 0, 0);
      const ed = new Date(now.getFullYear(), now.getMonth(), now.getDate(), eh || 0, em || 0, 0);
      let diff = (ed.getTime() - sd.getTime()) / 60000; // minutes
      if (diff <= 0) diff = Math.abs(diff); // fallback
      if (diff >= 60) {
        const h = Math.floor(diff / 60);
        const m = Math.round(diff % 60);
        return m === 0 ? `${h}h` : `${h}h ${m}m`;
      }
      return `${Math.round(diff)}m`;
    } catch (err) {
      return '';
    }
  };

  return (
    <Container maxWidth="xl">
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', width: '100%' }}>
        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
          Welcome, {memberProfile?.firstName || user?.firstName}!
        </Typography>
        {memberProfile && (
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            {`Membership #${memberProfile.membershipNumber}`}
          </Typography>
        )}
      </Paper>

      {/* Failed Transactions Warning */}
      {!failedTransactionsLoading && hasFailedTransactions && (
        <Alert 
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button 
              color="inherit" 
              size="small"
              onClick={() => navigate('/billing')}
            >
              View Billing
            </Button>
          }
        >
          <Typography variant="body2" >
            We have encountered issues processing payments on your account. <br />
            Please review your transactions & update your payment information as needed. 
            <br />
          </Typography>
        </Alert>
      )}

      {/* Quick Actions Section */}
      <Paper sx={{ p: 2, mb: 3, width: '100%' }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}>
          Quick Actions
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleBookClass}
            sx={{
              flex: '1 1 calc(33.333% - 11px)',
              whiteSpace: 'normal',
              px: { xs: 2, sm: 3 },
              py: { xs: 0.75, sm: 1 },
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              borderColor: '#764ba2',
              color: '#764ba2',
              '&:hover': {
                borderColor: '#667eea',
                backgroundColor: 'rgba(118, 75, 162, 0.04)',
              },
            }}
          >
            Book Class
          </Button>
          <Button
            variant="outlined"
            onClick={handleBookCourt}
            sx={{
              flex: '1 1 calc(33.333% - 11px)',
              whiteSpace: 'normal',
              px: { xs: 2, sm: 3 },
              py: { xs: 0.75, sm: 1 },
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              borderColor: '#764ba2',
              color: '#764ba2',
              '&:hover': {
                borderColor: '#667eea',
                backgroundColor: 'rgba(118, 75, 162, 0.04)',
              },
            }}
          >
            Book Court
          </Button>
          <Button
            variant="outlined"
            onClick={handleTodaysSchedule}
            sx={{
              flex: '1 1 calc(33.333% - 11px)',
              whiteSpace: 'normal',
              px: { xs: 2, sm: 3 },
              py: { xs: 0.75, sm: 1 },
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              borderColor: '#764ba2',
              color: '#764ba2',
              '&:hover': {
                borderColor: '#667eea',
                backgroundColor: 'rgba(118, 75, 162, 0.04)',
              },
            }}
          >
            Today's Schedule
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={3} sx={{ width: '100%' }}>

        {/* My Schedule (Next 7 Days) */}
        <Grid size={{ xs: 12, md: 6 }} >
          <Paper
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 400
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                My Schedule ({todaysBookings.length})
              </Typography>
              <Box>
                <IconButton size="small" onClick={refetch} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
                {todaysBookings.length > 3 && (
                  <IconButton size="small" onClick={() => setTodaysExpanded(!todaysExpanded)}>
                    {todaysExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                )}
              </Box>
            </Box>

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={32} />
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {!loading && !error && todaysBookings.length === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                <EventAvailableIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                  You have no upcoming bookings
                </Typography>
              </Box>
            )}

            {!loading && !error && todaysBookings.length > 0 && (
              <Box sx={{ flexGrow: 1 }}>
                <List sx={{ p: 0 }}>
                  {todaysBookings
                    .sort((a, b) => {
                      // Sort by date first, then by time
                      // Ensure proper date parsing by creating full ISO strings
                      const dateTimeA = `${a.date}T${a.startTime || '00:00:00'}`;
                      const dateTimeB = `${b.date}T${b.startTime || '00:00:00'}`;
                      const dateA = new Date(dateTimeA);
                      const dateB = new Date(dateTimeB);
                      return dateA.getTime() - dateB.getTime();
                    })
                    .slice(0, todaysExpanded ? undefined : 3)
                    .map((booking) => (
                      <BookingCard key={booking.id} booking={booking} isCompact />
                    ))}
                </List>
                {todaysBookings.length > 3 && !todaysExpanded && (
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Button
                      size="small"
                      onClick={() => setTodaysExpanded(true)}
                      startIcon={<ExpandMore />}
                      variant="text"
                      sx={{ color: 'primary.main' }}
                    >
                      Load More ({todaysBookings.length - 3})
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Today's Upcoming Classes */}
        <Grid size={{ xs: 12, md: 6 }} >
          <Paper
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 400
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                    <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
                    Upcoming Classes
                  </Typography>
                </Box>

                <Box>
                  <IconButton size="small" onClick={fetchTodaysClasses} disabled={classesLoading}>
                    <RefreshIcon />
                  </IconButton>
                  {todaysClasses.length > 5 && (
                    <IconButton size="small" onClick={() => setUpcomingExpanded(!upcomingExpanded)}>
                      {upcomingExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Box>

            {classesLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={32} />
              </Box>
            )}

            {classesError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {classesError}
              </Alert>
            )}

            {!classesLoading && !classesError && todaysClasses.length === 0 && (
              <>
                <Typography variant="body2" color="text.secondary">
                  There are no upcoming classes scheduled for today or tomorrow.
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Browse available classes to book or check the schedule.
                  </Typography>
                </Box>
              </>
            )}

            {!classesLoading && !classesError && todaysClasses.length > 0 && (
              <Box sx={{ flexGrow: 1 }}>
                <List sx={{ p: 0 }}>
                  {(upcomingExpanded ? todaysClasses : todaysClasses.slice(0, 5)).map((instance: any) => {
                    // ClassInstance has these fields directly
                    const startTime = instance.startTime || '';
                    const endTime = instance.endTime || '';
                    const duration = computeDurationDisplay(startTime, endTime);
                    const coach = instance.instructor || instance.parentClass?.instructorName || '';
                    const capacity = instance.capacity || 0;
                    // enrolledCount comes from includeBookings parameter
                    const booked = instance.enrolledCount || 0;
                    const spotsLeft = capacity > booked ? capacity - booked : 0;
                    const isFull = capacity > 0 && spotsLeft <= 0;

                    // determine event type color
                    const typeKey = (instance.type || 'class').toString().toLowerCase();
                    const typeColorMap: Record<string, string> = {
                      class: '#1976d2',
                      cardio: '#1976d2',
                      session: '#8e24aa',
                      program: '#2e7d32',
                      workshop: '#f57c00',
                      squash: '#e91e63',
                      tennis: '#4caf50',
                      pickleball: '#ff9800'
                    };
                    const barColor = typeColorMap[typeKey] || '#6c757d';

                    const handleClick = () => {
                      // Navigate to the member class booking page
                      navigate(`/member/class-booking/${instance.id}`);
                    };

                    // Format the date for display
                    const instanceDate = instance.sessionDate;
                    const dateObj = instanceDate ? new Date(instanceDate + 'T00:00:00') : null;
                    const isToday = dateObj && formatDateForInput(dateObj) === formatDateForInput(new Date());
                    const isTomorrow = dateObj && formatDateForInput(dateObj) === formatDateForInput(new Date(Date.now() + 86400000));
                    const dateLabel = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : dateObj ? formatDate(instanceDate) : '';

                    return (
                      <ListItem key={instance.id} divider sx={{ alignItems: 'stretch', p: 0 }}>
                        <ListItemButton onClick={handleClick} sx={{ width: '100%', alignItems: 'stretch', p: 2 }}>
                          <Box sx={{ display: 'flex', width: '100%' }}>
                            {/* left time column */}
                            <Box sx={{ width: 88, display: 'flex', flexDirection: 'column', alignItems: 'center', pr: 1 }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left', pl: 2 }}>
                                {dateLabel && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.25 }}>{dateLabel}</Typography>}
                                <Typography variant="subtitle2">{formatTimeDisplay(startTime)}</Typography>
                                {duration ? <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>{duration}</Typography> : null}
                              </Box>
                            </Box>

                            {/* colored separator bar between time and details */}
                            <Box sx={{ width: 8, backgroundColor: barColor, borderRadius: 1, mx: 1, alignSelf: 'stretch' }} />

                            {/* details */}
                            <Box sx={{ flexGrow: 1 }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                <Box>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{instance.name}</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {coach ? `${coach}` : ''}{instance.venue ? ` • ${instance.venue}` : ''}
                                  </Typography>
                                  <Box sx={{ mt: 0.5 }}>
                                    {instance.level && <Chip label={instance.level} size="small" sx={{ mr: 1 }} />}
                                    {instance.type && <Chip label={instance.type} size="small" sx={{ mr: 1 }} />}
                                  </Box>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  {capacity > 0 ? (
                                    <Chip label={isFull ? 'Full' : `Spots: ${spotsLeft}`} size="small" color={isFull ? 'error' : 'success'} />
                                  ) : (
                                    <Chip label="Open" size="small" />
                                  )}
                                  {instance.costOverride && <Typography variant="caption" display="block">${instance.costOverride}</Typography>}
                                </Box>
                              </Stack>
                            </Box>
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
                {todaysClasses.length > 5 && !upcomingExpanded && (
                  <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Button size="small" onClick={() => setUpcomingExpanded(true)}>
                      Show {todaysClasses.length - 5} more
                    </Button>
                  </Box>
                )}

                {/* View all link when expanded or too many items */}
                {todaysClasses.length > 5 && (
                  <Box sx={{ mt: 2, textAlign: 'right' }}>
                    <Button component={RouterLink} to="/classes" size="small">View all</Button>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Membership Status */}
        {/* <Grid size={{ xs: 12, md: 6 }} >
          <Paper
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              height: 200,
              bgcolor: (memberProfile?.status || user?.status) === 'active' ? 'success.light' : 'warning.light'
            }}
          >
            <Typography variant="h6" gutterBottom>
              Membership Status
            </Typography>
            <Typography variant="body1">
              Status: <strong>{memberProfile?.status?.toUpperCase() || user?.status?.toUpperCase()}</strong>
            </Typography>
            <Typography variant="body1">
              Membership Type: <strong>{memberProfile?.membershipTier?.name || user?.membershipTierName || 'N/A'}</strong>
            </Typography>
            <Typography variant="body1">
              Expiration: <strong>{memberProfile?.endDate ? new Date(memberProfile.endDate).toLocaleDateString() : (user?.membershipExpiryDate ? new Date(user.membershipExpiryDate).toLocaleDateString() : 'N/A')}</strong>
            </Typography>
          </Paper>
        </Grid> */}

        {/* Recent Activity */}
        {/* <Grid size={{ xs: 12, md: 6 }} >
          <Paper
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              height: 200
            }}
          >
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <Typography variant="body1">
              Last Access: <strong>{user?.lastAccessTime ? new Date(user.lastAccessTime).toLocaleString() : 'N/A'}</strong>
            </Typography>
            <Typography variant="body1">
              Facility Access Count: <strong>{user?.facilityAccessCount || 0}</strong>
            </Typography>
          </Paper>
        </Grid> */}
        {/* Membership QR Code */}
        {/* <Grid size={{ xs: 12, md: 6 }} >
          <Paper
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Typography variant="h6" gutterBottom>
              Membership Card
            </Typography>
            <Typography variant="body2" gutterBottom>
              Show this QR code at the facility entrance for quick check-in.
            </Typography>
            <Box sx={{ mt: 2, mb: 2, width: 200, height: 200, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                QR Code will appear here
              </Typography>
            </Box>
          </Paper>
        </Grid> */}
      </Grid>

      {/* Member Alert Dialog */}
      <Dialog open={alertDialogOpen} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogTitle sx={{ fontWeight: 'bold', color: 'warning.main' }}>Important Notice</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
            {memberProfile?.alertNote || 'Please speak with a staff member.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setAlertDialogOpen(false)}>
            Acknowledged
          </Button>
        </DialogActions>
      </Dialog>

      {/* Court Booking Dialog */}
      <Dialog open={courtDialogOpen} onClose={handleCourtDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Book a Court</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <FormControl fullWidth required>
              <InputLabel id="court-type-label">Court Type</InputLabel>
              <Select
                labelId="court-type-label"
                id="court-type-select"
                value={courtType}
                label="Court Type"
                onChange={(e) => setCourtType(e.target.value)}
              >
                <MenuItem value="Tennis">Tennis</MenuItem>
                <MenuItem value="Pickleball">Pickleball</MenuItem>
                <MenuItem value="Squash">Squash</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Date"
              type="date"
              value={courtDate}
              onChange={(e) => setCourtDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />

            <TextField
              label="Preferred Time (Optional)"
              type="time"
              value={courtTime}
              onChange={(e) => setCourtTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              helperText="Leave blank to view all available times"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCourtDialogClose}>Cancel</Button>
          <Button
            onClick={handleCourtBookingSubmit}
            variant="contained"
            disabled={!courtType || !courtDate}
          >
            Find Courts
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MemberDashboard;
